import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import anime from 'animejs'

import KeyIssuePreview from '../key-issues/components/key-issue-preview'
import KeyIssueChip from '../key-issues/components/key-issue-chip'
import MarkdownWrapper from '../utils/markdown-wrapper'
import BackgroundSVG from './background-svg'
import RelatedSlider from '../layout/related-slider'
import { saveNavTheme } from '../../reducers/nav'
import { ReactComponent as MarkerStroke } from '../../assets/svgs/line.svg'

const selectKeyIssues = state => state.keyIssues
const selectChapter = (state, ownProps) => ownProps.chapter
const makeRelatedIssues = createSelector(
    [selectKeyIssues, selectChapter],
    (keyIssues, chapter) => {
        if (chapter?.keyIssueIds) {
            return keyIssues.filter(ki =>
                chapter.keyIssueIds.includes(ki.uuid)
            )
        }
        return []
    }
)

const mapStateToProps = (state, ownProps) => {
    const relatedIssues = makeRelatedIssues(state, ownProps)
    return {
        relatedIssues,
        uiDeviceType: state.ui.deviceType
    }
}

const CharacterChapter = ({ dispatch, characterName, chapter, characterKeyIssue, relatedIssues, status, uiDeviceType }) => {
    const [showKeyIssues, setShowKeyIssues] = useState(false)
    const [enableLinks, setEnableLinks] = useState(false)
    const color = characterKeyIssue ? characterKeyIssue.color : 'grey'

    useEffect(() => {
        if (status === 'ACTIVE') {
            dispatch(saveNavTheme('grey'))
            setEnableLinks(true)
        } else if (status === 'PREVIOUS' || status === 'NEXT') {
            setTimeout(() => {
                setEnableLinks(false)
            }, 500)
        }
    }, [status])

    useEffect(() => {
        if (showKeyIssues) {
            // if mobile, lets scroll back to the top
            if (uiDeviceType === 'mobile' || uiDeviceType === 'tablet') {
                let t = document.querySelector('.character-content-right-panel')
                t.scrollTo(0, 0)
            }
            let triggerNav = true
            anime.timeline({
                duration: 450,
                easing: 'easeInOutQuad',
                update: function (anim) {
                    let perc = Math.round(anim.progress)
                    if (perc > 50 && triggerNav) {
                        triggerNav = false
                        let navActions = document.querySelector('.nav-actions')
                        if (navActions) {
                            navActions.classList.add('inactive')
                        }
                    }
                }
            })
                .add({
                    targets: '.nav-actions',
                    marginLeft: '-60rem',
                    duration: 200,
                    easing: 'easeInOutQuad'
                })
                .add({
                    targets: '.key-issue-back',
                    bottom: '7rem',
                    duration: 200,
                    easing: 'easeInOutQuad'
                }, '+=300')
                .add({
                    targets: '.key-issue-preview',
                    translateY: 0,
                    opacity: 1,
                    delay: function (el, i) {
                        return i * anime.random(0, 250)
                    }
                }, '+=400')
            anime.timeline({
                duration: 450,
                easing: 'easeInOutQuad'
            })
                .add({
                    targets: '.key-issue-text',
                    width: '50%'
                })
                .add({
                    targets: '.key-issue-text',
                    opacity: 1
                })
        } else {
            anime({
                duration: 100,
                targets: '.key-issue-preview',
                translateY: '10px',
                opacity: 0
            })
            anime.timeline({
                duration: 100,
                easing: 'easeInOutQuad',
                begin: function (anim) {
                    // reenable nav actions as tab still gets focus
                    let navActions = document.querySelector('.nav-actions')
                    if (navActions) {
                        navActions.classList.remove('inactive')
                    }
                }
            })
                .add({
                    targets: '.key-issue-back',
                    bottom: '-10rem',
                    duration: 100
                })
                .add({
                    targets: '.key-issue-text',
                    opacity: 0,
                    width: 0
                })
                .add({
                    targets: '.nav-actions',
                    marginLeft: '0',
                    duration: 300
                }, '+=400')
        }
    }, [showKeyIssues])

    if (!chapter) {
        return null
    }

    const renderRelatedIssueChips = () => {
        return relatedIssues.map((issue, index) => {
            return <KeyIssueChip showKeyIssues={showKeyIssues} onClickEvent={() => setShowKeyIssues(!showKeyIssues)} key={index} classes={`issue-${index + 1}`} keyIssue={issue} />
        })
    }

    const renderRelatedIssuesPreviews = () => {
        if (relatedIssues.length === 0) {
            return null
        }
        let issues = relatedIssues.map((issue, index) => {
            return <KeyIssuePreview key={index} keyIssue={issue} />
        })
        if (uiDeviceType === 'mobile' || uiDeviceType === 'tablet') {
            return issues
        }
        let slidesToShowOverride = {
            default: 5,
            intermediate: 3.5,
            laptop: 4.5,
            desktop: 5
        }
        return (
            <RelatedSlider classes="related-character-key-issues" slidesToShowOverride={slidesToShowOverride} uiDeviceType={uiDeviceType}>
                {issues}
            </RelatedSlider>
        )
    }

    if (status === 'OFF') {
        return null
    }
    let keyIssueContent = chapter.keyIssueContent ? chapter.keyIssueContent : <p>Explore key issues in this character\'s story.</p>

    if (uiDeviceType === 'mobile' || uiDeviceType === 'tablet') {
        return (
            <div className={`chapter-container character-chapter ${status.toLowerCase()} ${enableLinks ? '' : 'disable-links'} ${showKeyIssues ? 'reveal-key-issues' : ''}`}>
                <div className="character-content">
                    <div className="character-content-left-panel">
                        <div className="content">
                            <h2 className="chapter-title">{ chapter.title }</h2>
                            <MarkdownWrapper content={chapter.content} />
                        </div>
                        <div className="character-key-issues">
                            <div className="key-issue-chip-wrapper">
                                <div className="key-issue-image">
                                    <BackgroundSVG shape={chapter.svg_shape?.url} bgColor={color} />
                                    <img src={chapter.primary_image?.url} alt={chapter.primary_image?.alt} />
                                    {renderRelatedIssueChips()}
                                    <button data-id="Explore KI Button" className={`key-issue-show btn small ${color}-bg ${!showKeyIssues ? '' : 'inactive'}`} onClick={() => setShowKeyIssues(!showKeyIssues)}>
                                        Explore Key Issues
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="character-content-right-panel">
                        <div className="key-issue-chip-wrapper">
                            <div className="key-issue-image">
                                <div className="key-issue-image-wrapper">
                                    <BackgroundSVG shape={chapter.svg_shape?.url} bgColor={color} />
                                    <img src={chapter.primary_image?.url} alt={chapter.primary_image?.alt} />
                                </div>
                                <div className="title-stroke">
                                    <h3>Key issues in {characterName}'s Tale</h3>
                                    <MarkerStroke />
                                </div>
                            </div>
                            <div className="key-issue-text-mobile">
                                <MarkdownWrapper content={keyIssueContent} />
                            </div>
                        </div>
                        <div className={`key-issue-summaries ${showKeyIssues ? '' : 'inactive'}`}>
                            {renderRelatedIssuesPreviews()}
                        </div>
                        <button className={`key-issue-back btn small ${showKeyIssues ? '' : 'inactive'}`} onClick={() => setShowKeyIssues(!showKeyIssues)}>
                            Resume Story
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={`chapter-container character-chapter ${status.toLowerCase()} ${enableLinks ? '' : 'disable-links'} ${showKeyIssues ? 'show-key-issues' : ''}`}>
            <div className="character-content">
                <div className="content">
                    <h2 className="chapter-title">{ chapter.title }</h2>
                    <MarkdownWrapper content={chapter.content} />
                </div>
                <div className="character-key-issues">
                    <div className="key-issue-chip-wrapper">
                        <div className="key-issue-image">
                            <BackgroundSVG shape={chapter.svg_shape?.url} bgColor={color} />
                            <img src={chapter.primary_image?.url} alt={chapter.primary_image?.alt} />
                            {renderRelatedIssueChips()}
                            <button className={`key-issue-show btn small ${color}-bg ${!showKeyIssues ? '' : 'inactive'}`} onClick={() => setShowKeyIssues(!showKeyIssues)}>
                                Explore Key Issues
                            </button>
                        </div>
                        <div className="key-issue-text">
                            <div className="title-stroke">
                                <h3>Key issues in {characterName}'s Tale</h3>
                                <MarkerStroke />
                            </div>
                            <MarkdownWrapper content={keyIssueContent} />
                        </div>
                    </div>
                    <div className={`key-issue-summaries ${showKeyIssues ? '' : 'inactive'}`}>
                        {renderRelatedIssuesPreviews()}
                    </div>
                    <button className={`key-issue-back btn small ${showKeyIssues ? '' : 'inactive'}`} onClick={() => setShowKeyIssues(!showKeyIssues)}>
                        Resume Story
                    </button>
                </div>
            </div>
        </div>
    )
}

export default connect(mapStateToProps)(CharacterChapter)
